import { useSearch } from '@tanstack/react-router';
import { FileText, Flag, Plus, Receipt, SearchX } from 'lucide-react';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Medida } from '@/componentes/Medida';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { Seletor } from '@/componentes/Seletor';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { diaDaOperacao } from '@/lib/dia-da-operacao';
import { formatarDataHora, formatarDia, formatarInteiro, formatarReais } from '@/lib/formato';

import { useContingencias, useNotasDaIntegracao, useTituloDaIntegracao, useTitulosDaIntegracao } from './api';
import {
  ORIGEM_DA_CONTA,
  ROTULO_DA_ENTIDADE,
  chaveEmGrupos,
  quemLancaOTitulo,
  validarBuscaDosTitulos,
  vencimentoEscrito,
  type Contingencia,
  type NotaDaIntegracao,
  type TituloDaIntegracao,
} from './integracoes';
import { GavetaDaNota, GavetaDoTitulo, JanelaDaBaixa, JanelaDoCancelamentoDoTitulo } from './JanelasDosTitulos';
import { ErroDaIntegracao, HubDasIntegracoes } from './PaginaIntegracoes';
import { usePermissoesDaIntegracao } from './permissoes';
import { SeloDaIntegracao } from './Selo';

/**
 * Módulo 04, fatia b (0108) — TÍTULOS E NOTAS no hub das integrações: os
 * títulos a pagar e a receber com as baixas, as notas só como referência e as
 * escritas em contingência (a regra I6, "quem não é mestre não escreve"), com
 * o lote do mestre que as casou.
 *
 * O manual não desenha a fatia b do 04 (o mapa das p.50 a p.60 só tem a
 * conciliação de horas): segue a lista com detalhe (p.38), o formulário na
 * gaveta (p.29) e a janela da baixa (p.30) — dúvida de design registrada.
 */

type Vista = 'a_pagar' | 'a_receber' | 'notas' | 'contingencias';

const reais = (v: number) => <span className="numero whitespace-nowrap text-tinta">{formatarReais(v)}</span>;

export function PaginaTitulosENotas() {
  const pode = usePermissoesDaIntegracao();
  /* 0109: o aviso do título vencido chega com a vista e o título no endereço. */
  const busca = validarBuscaDosTitulos(useSearch({ strict: false }));
  const [vista, setVista] = useState<Vista>(busca.vista ?? 'a_pagar');
  const [situacao, setSituacao] = useState<'em_aberto' | 'todos'>('em_aberto');
  const [pagina, setPagina] = useState(1);
  const [aberto, setAberto] = useState<string | null>(busca.titulo ?? null);
  const [lancando, setLancando] = useState(false);
  const [registrandoNota, setRegistrandoNota] = useState(false);
  const [baixando, setBaixando] = useState<TituloDaIntegracao | null>(null);
  const [cancelando, setCancelando] = useState<TituloDaIntegracao | null>(null);
  const ehTitulo = vista === 'a_pagar' || vista === 'a_receber';
  const titulos = useTitulosDaIntegracao(ehTitulo ? vista : undefined, situacao, pagina);
  const t = titulos.data;
  const hoje = t?.hoje ?? diaDaOperacao();
  const mestres = t?.mestres;
  const mestreDoTitulo = mestres?.[vista === 'a_receber' ? 'titulo_a_receber' : 'titulo_a_pagar'];

  const trocar = (v: Vista) => {
    setVista(v);
    setPagina(1);
    setAberto(null);
  };

  return (
    <>
      <HubDasIntegracoes
        acoes={
          <>
            {pode.contingencia ? <Botao nivel="secundario" onClick={() => { setRegistrandoNota(true); }}>Registrar nota</Botao> : null}
            {pode.gravarTitulo ? (
              <Botao nivel="primario" disabled={!t} onClick={() => { setLancando(true); }}>
                <Plus aria-hidden="true" className="size-[var(--icone-controle)]" />
                Lançar título
              </Botao>
            ) : null}
          </>
        }
      >
        <section className="cartao flex flex-col gap-2 p-6">
          <h2 className="txt-secao text-tinta">Quem lança</h2>
          {t ? (
            <ul className="flex flex-col gap-1">
              <li className="txt-apoio text-pretty text-tinta-secundaria">{quemLancaOTitulo(mestres?.titulo_a_pagar, 'títulos a pagar').frase}</li>
              <li className="txt-apoio text-pretty text-tinta-secundaria">{quemLancaOTitulo(mestres?.titulo_a_receber, 'títulos a receber').frase}</li>
              <li className="txt-apoio text-pretty text-tinta-secundaria">
                {mestres?.nota_de_entrada.mestre || mestres?.nota_de_saida.mestre
                  ? `As notas vêm de ${mestres.nota_de_entrada.mestre ?? mestres.nota_de_saida.mestre ?? ''}, só como referência: a plataforma não emite nota nem apura imposto.`
                  : 'As notas só entram como referência, do mestre de fora aprovado na matriz: a plataforma não emite nota nem apura imposto.'}
              </li>
            </ul>
          ) : (
            <div className="flex flex-col gap-2" aria-hidden="true">
              <span className="esqueleto block h-4 w-2/3" />
              <span className="esqueleto block h-4 w-1/2" />
            </div>
          )}
          {t ? (
            <p className="txt-apoio tabular-nums text-tinta">
              {`A pagar em aberto ${formatarReais(t.totais.aPagarEmAberto)} (vencido ${formatarReais(t.totais.aPagarVencido)}) · a receber em aberto ${formatarReais(t.totais.aReceberEmAberto)} (vencido ${formatarReais(t.totais.aReceberVencido)})`}
            </p>
          ) : null}
        </section>

        <div className="flex flex-wrap items-end gap-4">
          <Seletor<Vista>
            rotulo="O que ver"
            valor={vista}
            aoEscolher={trocar}
            opcoes={[
              { valor: 'a_pagar', rotulo: 'A pagar' },
              { valor: 'a_receber', rotulo: 'A receber' },
              { valor: 'notas', rotulo: 'Notas' },
              { valor: 'contingencias', rotulo: 'Contingências' },
            ]}
          />
          {ehTitulo ? (
            <Seletor<'em_aberto' | 'todos'>
              rotulo="Situação"
              valor={situacao}
              aoEscolher={(v) => { setSituacao(v); setPagina(1); }}
              opcoes={[{ valor: 'em_aberto', rotulo: 'Em aberto' }, { valor: 'todos', rotulo: 'Todos' }]}
            />
          ) : null}
        </div>

        {ehTitulo ? (
          <Tabela
            rotulo={vista === 'a_pagar' ? 'Títulos a pagar' : 'Títulos a receber'}
            colunas={colunasDosTitulos(hoje)}
            linhas={t?.titulos ?? []}
            chaveDaLinha={(x) => x.id}
            alturaDupla
            selecionada={aberto}
            aoSelecionar={(x) => { setAberto(x.id); }}
            carregando={titulos.isPending}
            erro={titulos.isError ? <ErroDaIntegracao oQue="os títulos" erro={titulos.error} aoTentar={() => { void titulos.refetch(); }} /> : null}
            vazio={
              situacao === 'em_aberto' ? (
                <EstadoVazio
                  icone={Receipt}
                  titulo={`Nenhum título ${vista === 'a_pagar' ? 'a pagar' : 'a receber'} em aberto`}
                  descricao={quemLancaOTitulo(mestreDoTitulo, 'títulos').externo
                    ? 'Os títulos chegam pela importação do arquivo do mestre, na aba Lotes.'
                    : 'Lance o primeiro título, ou aprove na matriz o sistema de fora que é o mestre deles.'}
                  acao={<Botao nivel="secundario" onClick={() => { setSituacao('todos'); }}>Ver todos</Botao>}
                />
              ) : (
                <EstadoVazio icone={SearchX} titulo="Nenhum título ainda" descricao="Os títulos aparecem aqui quando o lote do mestre é promovido, ou quando a plataforma lança." />
              )
            }
            rodape={
              <RodapeTabela
                primeiro={(t?.total ?? 0) === 0 ? 0 : (pagina - 1) * (t?.porPagina ?? 100) + 1}
                ultimo={Math.min(pagina * (t?.porPagina ?? 100), t?.total ?? 0)}
                total={t?.total ?? 0}
                pagina={pagina}
                paginas={Math.max(1, Math.ceil((t?.total ?? 0) / (t?.porPagina ?? 100)))}
                porPagina={t?.porPagina ?? 100}
                opcoesPorPagina={[t?.porPagina ?? 100]}
                aoMudarPagina={setPagina}
                aoMudarPorPagina={() => undefined}
              />
            }
          />
        ) : null}
        {vista === 'notas' ? <ListaDasNotas pagina={pagina} aoMudarPagina={setPagina} /> : null}
        {vista === 'contingencias' ? <ListaDasContingencias /> : null}
      </HubDasIntegracoes>

      {aberto && ehTitulo ? (
        <PainelDoTitulo
          id={aberto}
          hoje={hoje}
          aoFechar={() => { setAberto(null); }}
          aoBaixar={pode.gravarTitulo ? setBaixando : null}
          aoCancelar={pode.gravarTitulo ? setCancelando : null}
        />
      ) : null}
      {pode.gravarTitulo ? <GavetaDoTitulo aberta={lancando} aoFechar={() => { setLancando(false); }} mestres={mestres} hoje={hoje} /> : null}
      {pode.gravarTitulo ? <JanelaDaBaixa alvo={baixando} aoFechar={() => { setBaixando(null); }} mestre={mestres?.baixa} hoje={hoje} /> : null}
      {pode.gravarTitulo ? (
        <JanelaDoCancelamentoDoTitulo
          alvo={cancelando}
          aoFechar={() => { setCancelando(null); }}
          mestre={cancelando?.tipo === 'a_receber' ? mestres?.titulo_a_receber : mestres?.titulo_a_pagar}
        />
      ) : null}
      {pode.contingencia ? <GavetaDaNota aberta={registrandoNota} aoFechar={() => { setRegistrandoNota(false); }} mestres={mestres} hoje={hoje} /> : null}
    </>
  );
}

function colunasDosTitulos(hoje: string): readonly Coluna<TituloDaIntegracao>[] {
  return [
    {
      chave: 'titulo',
      rotulo: 'Título',
      prioridade: 'chave',
      celula: (x) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{x.numero}</span>
          <TextoDaCelula texto={`${x.contraparte ?? ''}${x.pedido ? ` · pedido ${x.pedido}` : ''}${x.documento ? ` · cobrança ${x.documento}` : ''}`} classe="txt-apoio text-tinta-secundaria" linhas={1} />
        </span>
      ),
    },
    {
      chave: 'vencimento',
      rotulo: 'Vencimento',
      celula: (x) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{formatarDia(x.vencimento)}</span>
          <TextoDaCelula
            texto={x.canceladoEm ? 'cancelado' : vencimentoEscrito(x.vencimento, hoje, x.emAberto)}
            classe={`txt-apoio ${!x.canceladoEm && x.emAberto > 0 && x.vencimento < hoje ? 'text-atencao-texto' : 'text-tinta-secundaria'}`}
            linhas={1}
          />
        </span>
      ),
    },
    { chave: 'valor', rotulo: 'Valor', alinhamento: 'numero', celula: (x) => reais(x.valor) },
    { chave: 'aberto', rotulo: 'Em aberto', alinhamento: 'numero', celula: (x) => reais(x.canceladoEm ? 0 : x.emAberto) },
    {
      chave: 'origem',
      rotulo: 'Origem',
      alinhamento: 'status',
      prioridade: 'secundaria',
      celula: (x) => (x.canceladoEm ? <SeloDaIntegracao rotulo="Cancelado" tom="neutro" /> : <SeloDaIntegracao {...ORIGEM_DA_CONTA[x.origem]} />),
    },
  ];
}

function PainelDoTitulo({ id, hoje, aoFechar, aoBaixar, aoCancelar }: {
  readonly id: string;
  readonly hoje: string;
  readonly aoFechar: () => void;
  readonly aoBaixar: ((t: TituloDaIntegracao) => void) | null;
  readonly aoCancelar: ((t: TituloDaIntegracao) => void) | null;
}) {
  const detalhe = useTituloDaIntegracao(id);
  const d = detalhe.data;
  const t = d?.titulo;
  const vivo = t !== undefined && t.canceladoEm === null;
  return (
    <PainelDetalhe
      codigo={t?.numero ?? ''}
      titulo={t ? `${t.tipo === 'a_pagar' ? 'A pagar' : 'A receber'} · ${t.contraparte ?? ''}` : 'Título'}
      subtitulo={t ? `${formatarReais(t.valor)}, ${vencimentoEscrito(t.vencimento, hoje, t.emAberto)}` : ''}
      marcadores={t ? (t.canceladoEm ? <SeloDaIntegracao rotulo="Cancelado" tom="neutro" /> : <SeloDaIntegracao {...ORIGEM_DA_CONTA[t.origem]} />) : undefined}
      dadosChave={
        t ? (
          <>
            <DadoChave rotulo="Emissão">{formatarDia(t.emitidoEm)}</DadoChave>
            <DadoChave rotulo="Vencimento">{formatarDia(t.vencimento)}</DadoChave>
            <DadoChave rotulo="Baixado"><Medida valor={formatarReais(t.baixado)} unidade="" /></DadoChave>
            <DadoChave rotulo="Em aberto"><Medida valor={formatarReais(t.emAberto)} unidade="" /></DadoChave>
            <DadoChave rotulo="Ligado a">{t.pedido ? `Pedido ${t.pedido}` : t.documento ? `Cobrança ${t.documento}` : 'Sem ligação'}</DadoChave>
            <DadoChave rotulo="Do sistema">{t.sistema ? `${t.sistema} · ${t.chaveExterna ?? ''}` : 'Lançado na plataforma'}</DadoChave>
            {t.motivoContingencia ? <DadoChave rotulo="Contingência">{t.motivoContingencia}</DadoChave> : null}
            {t.motivoCancelamento ? <DadoChave rotulo="Cancelamento">{t.motivoCancelamento}</DadoChave> : null}
          </>
        ) : (
          <span className="esqueleto block h-5 w-2/3" aria-hidden="true" />
        )
      }
      acoes={
        t && vivo
          ? [
            ...(aoBaixar && t.emAberto > 0 ? [<Botao key="baixa" nivel="secundario" tamanho="p" onClick={() => { aoBaixar(t); }}>Registrar baixa</Botao>] : []),
            ...(aoCancelar && t.baixado === 0 ? [<Botao key="cancelar" nivel="fantasma" tamanho="p" onClick={() => { aoCancelar(t); }}>Cancelar título</Botao>] : []),
          ]
          : []
      }
      abas={[
        {
          id: 'baixas',
          rotulo: `Baixas (${formatarInteiro(d?.baixas.length ?? 0)})`,
          conteudo: detalhe.isError ? (
            <ErroDaIntegracao oQue="o título" erro={detalhe.error} aoTentar={() => { void detalhe.refetch(); }} />
          ) : !d ? (
            <span className="esqueleto block h-5 w-1/2" aria-hidden="true" />
          ) : d.baixas.length === 0 ? (
            <EstadoVazio icone={Receipt} titulo="Nenhuma baixa" descricao="A baixa vem do mestre pela importação, ou é registrada aqui quando a plataforma é a mestre." />
          ) : (
            <ul className="flex flex-col" aria-label="Baixas do título">
              {d.baixas.map((b) => (
                <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-borda-sutil py-3 first:border-t-0 first:pt-0">
                  <span className="flex min-w-0 flex-col">
                    <span className="txt-corpo tabular-nums text-tinta">{`${formatarReais(b.valor)} em ${formatarDia(b.pagoEm)}`}</span>
                    <span className="txt-apoio text-pretty text-tinta-secundaria">
                      {b.chaveExterna ? `chave ${b.chaveExterna}` : 'lançada na plataforma'}
                      {b.motivoContingencia ? ` · contingência: ${b.motivoContingencia}` : ''}
                      {b.motivoCancelamento ? ` · cancelada: ${b.motivoCancelamento}` : ''}
                    </span>
                  </span>
                  {b.canceladaEm ? <SeloDaIntegracao rotulo="Cancelada" tom="neutro" /> : <SeloDaIntegracao {...ORIGEM_DA_CONTA[b.origem]} />}
                </li>
              ))}
            </ul>
          ),
        },
      ]}
      aoFechar={aoFechar}
    />
  );
}

function ListaDasNotas({ pagina, aoMudarPagina }: { readonly pagina: number; readonly aoMudarPagina: (p: number) => void }) {
  const notas = useNotasDaIntegracao(undefined, pagina);
  const n = notas.data;
  const colunas: readonly Coluna<NotaDaIntegracao>[] = [
    {
      chave: 'nota',
      rotulo: 'Nota',
      prioridade: 'chave',
      celula: (x) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{`${x.numero}${x.serie ? `-${x.serie}` : ''}`}</span>
          <TextoDaCelula texto={`${x.tipo === 'entrada' ? 'Entrada' : 'Saída'} · ${x.contraparte ?? ''}${x.pedido ? ` · pedido ${x.pedido}` : ''}${x.documento ? ` · cobrança ${x.documento}` : ''}`} classe="txt-apoio text-tinta-secundaria" linhas={1} />
        </span>
      ),
    },
    { chave: 'chave', rotulo: 'Chave de acesso', prioridade: 'secundaria', celula: (x) => <TextoDaCelula texto={chaveEmGrupos(x.chaveAcesso)} classe="mono txt-apoio text-tinta" linhas={2} /> },
    { chave: 'emissao', rotulo: 'Emissão', celula: (x) => <span className="txt-corpo text-tinta">{formatarDia(x.emitidaEm)}</span> },
    { chave: 'valor', rotulo: 'Valor', alinhamento: 'numero', celula: (x) => reais(x.valor) },
    {
      chave: 'origem',
      rotulo: 'Origem',
      alinhamento: 'status',
      celula: (x) => (x.canceladaEm ? <SeloDaIntegracao rotulo="Cancelada" tom="neutro" /> : <SeloDaIntegracao {...ORIGEM_DA_CONTA[x.origem]} />),
    },
  ];
  return (
    <Tabela
      rotulo="Notas como referência"
      colunas={colunas}
      linhas={n?.notas ?? []}
      chaveDaLinha={(x) => x.id}
      alturaDupla
      carregando={notas.isPending}
      erro={notas.isError ? <ErroDaIntegracao oQue="as notas" erro={notas.error} aoTentar={() => { void notas.refetch(); }} /> : null}
      vazio={<EstadoVazio icone={FileText} titulo="Nenhuma nota ainda" descricao="A referência da nota chega pela importação do mestre, ou é registrada em contingência. A plataforma não emite nota." />}
      rodape={
        <RodapeTabela
          primeiro={(n?.total ?? 0) === 0 ? 0 : (pagina - 1) * (n?.porPagina ?? 100) + 1}
          ultimo={Math.min(pagina * (n?.porPagina ?? 100), n?.total ?? 0)}
          total={n?.total ?? 0}
          pagina={pagina}
          paginas={Math.max(1, Math.ceil((n?.total ?? 0) / (n?.porPagina ?? 100)))}
          porPagina={n?.porPagina ?? 100}
          opcoesPorPagina={[n?.porPagina ?? 100]}
          aoMudarPagina={aoMudarPagina}
          aoMudarPorPagina={() => undefined}
        />
      }
    />
  );
}

function ListaDasContingencias() {
  const [situacao, setSituacao] = useState<'pendente' | 'casada'>('pendente');
  const lista = useContingencias(situacao);
  const colunas: readonly Coluna<Contingencia>[] = [
    {
      chave: 'referencia',
      rotulo: 'Escrita',
      prioridade: 'chave',
      celula: (x) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{`${ROTULO_DA_ENTIDADE[x.entidade]} ${x.referencia ?? ''}`}</span>
          <TextoDaCelula texto={`${x.operacao === 'inclusao' ? 'incluída' : 'alterada'} por ${x.feitaPor ?? '—'} em ${formatarDataHora(new Date(x.feitaEm))} · mestre ${x.mestre}`} classe="txt-apoio text-tinta-secundaria" linhas={1} />
        </span>
      ),
    },
    { chave: 'motivo', rotulo: 'Motivo', quebra: 'duas-linhas', celula: (x) => <TextoDaCelula texto={x.motivo} classe="txt-apoio text-tinta" linhas={2} /> },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      alinhamento: 'status',
      celula: (x) => (x.casadaEm ? <SeloDaIntegracao rotulo={`Casada · ${x.casadaPeloLote ?? ''}`} tom="sucesso" /> : <SeloDaIntegracao rotulo="Esperando o lote" tom="atencao" />),
    },
  ];
  return (
    <>
      <Seletor<'pendente' | 'casada'>
        rotulo="Contingências"
        valor={situacao}
        aoEscolher={setSituacao}
        opcoes={[{ valor: 'pendente', rotulo: 'Esperando o lote' }, { valor: 'casada', rotulo: 'Casadas' }]}
      />
      <Tabela
        rotulo="Escritas em contingência"
        colunas={colunas}
        linhas={lista.data?.contingencias ?? []}
        chaveDaLinha={(x) => x.id}
        alturaDupla
        carregando={lista.isPending}
        erro={lista.isError ? <ErroDaIntegracao oQue="as contingências" erro={lista.error} aoTentar={() => { void lista.refetch(); }} /> : null}
        vazio={
          situacao === 'pendente' ? (
            <EstadoVazio icone={Flag} titulo="Nenhuma contingência esperando o lote" descricao="Com o mestre de fora, a plataforma só escreve em contingência, com o motivo. Ela aparece aqui até o lote do mestre casar." />
          ) : (
            <EstadoVazio icone={SearchX} titulo="Nenhuma contingência casada" descricao="Ela é casada quando o lote do mestre traz o mesmo registro." acao={<Botao nivel="secundario" onClick={() => { setSituacao('pendente'); }}>Ver as pendentes</Botao>} />
          )
        }
      />
    </>
  );
}
